/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import idx from 'idx';

import ChatActions from 'chat/actions/ChatActions';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import Checkbox from 'components/Checkbox.jsx';
import EventTracking from 'lib/EventTracking.js';
import Legend from 'components/Legend.jsx';
import SettingsContainer from './SettingsContainer.jsx';
import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal';

type Props = {
  mutate: ({ variables: { input: boolean } }) => Promise<{ data: Object }>,
  data: ReactApolloData<TwoWayChatPreferenceQuery>,
};

class TwoWayChatPreference extends React.Component<Props> {
  handleTwoWayMessagingChange = (event: *) => {
    const twoWayMessaging = event.target.checked;
    const savePreference = () => {
      const saveOptions = [this.props.mutate({ variables: { input: twoWayMessaging } })];
      saveOptions.push(
        twoWayMessaging
          ? ChatActions.enableRepliesForAllChats()
          : ChatActions.disableRepliesForAllChats()
      );
      Promise.all(saveOptions)
        .then(() => ToastActions.showSuccessToast('Message options have been saved!'))
        .catch(ToastActions.showDefaultErrorToast);
    };

    const modal = (
      <ConfirmDialog
        eventScreenName="TwoWayChatPreference"
        title={
          'Are you sure you want to prevent people from replying to your announcements and starting conversations with you?'
        }
        subtext={'This will apply to all existing and new conversations.'}
        actionButtonText="Prevent replies"
        cancelButtonText="Cancel"
        size="small"
        onConfirm={() => {
          savePreference();
          EventTracking.trackKey(
            'account_settings.disable_two_way_confirm_modal.prevent_replies.click'
          );
        }}
      />
    );

    if (twoWayMessaging) {
      savePreference();
    } else {
      EventTracking.trackKey('account_settings.disable_two_way.checkbox.click');
      mountModal(modal, this);
    }
  };

  render() {
    return (
      <div>
        <Legend>
          Message options
          <p>Choose how you would like to communicate with your students and parents.</p>
        </Legend>
        <SettingsContainer>
          <Checkbox
            checked={idx(this.props.data.me, _ => _.preferences.two_way_messaging)}
            onChange={this.handleTwoWayMessagingChange}
            label="Allow replies to your messages"
          />
        </SettingsContainer>
      </div>
    );
  }
}

const withData = graphql(
  gql`
    query TwoWayChatPreference {
      me {
        id
        uuid
        preferences {
          two_way_messaging
        }
      }
    }
  `
);

const withMutation = graphql(
  gql`
    mutation UpdateTwoWayMessagingPref($input: Boolean!) {
      updateUserPrefs(input: { two_way_messaging: $input }) {
        user {
          uuid
          preferences {
            two_way_messaging
          }
        }
        error {
          message
          code
        }
      }
    }
  `
);

export { TwoWayChatPreference };
export default compose(
  withData,
  withMutation
)(TwoWayChatPreference);
